import { useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Pin } from 'lucide-react';
import type { AppDefinition } from '../types';
import { AppCard } from './AppCard';

export interface AppGridProps {
  apps: AppDefinition[];
  showStar: boolean;
  isFav: (id: string) => boolean;
  onToggleFav: (id: string) => void;
  /** Si fourni, active le drag & drop avec cet handler de réordonnancement */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  onTogglePin?: (id: string) => void;
  isPinned?: (id: string) => boolean;
  canPin?: boolean;
}

export function AppGrid({
  apps,
  showStar,
  isFav,
  onToggleFav,
  onReorder,
  onTogglePin,
  isPinned,
  canPin,
}: AppGridProps) {
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const dragNode = useRef<number | null>(null);

  const handleDragStart = (i: number) => {
    dragNode.current = i;
    requestAnimationFrame(() => setDraggedIdx(i));
  };

  const handleDragEnter = (i: number) => {
    if (dragNode.current !== i) setOverIdx(i);
  };

  const handleDragEnd = () => {
    if (onReorder && dragNode.current !== null && overIdx !== null) {
      onReorder(dragNode.current, overIdx);
    }
    setDraggedIdx(null);
    setOverIdx(null);
    dragNode.current = null;
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(80px,96px))]">
      {apps.map((app, i) => {
        const isDragging = draggedIdx === i;
        const isOver = overIdx === i && draggedIdx !== null && draggedIdx !== i;

        return (
          <div key={app.id} className="group/card relative">
            <div
              draggable={Boolean(onReorder)}
              onDragStart={() => handleDragStart(i)}
              onDragEnter={() => handleDragEnter(i)}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              className={[
                'rounded-xl transition-all duration-150',
                onReorder ? 'cursor-grab active:cursor-grabbing' : '',
                isDragging ? 'scale-95 opacity-30' : '',
                isOver
                  ? 'scale-105 ring-2 ring-brand-500 ring-offset-2 dark:ring-offset-slate-900'
                  : '',
              ].join(' ')}
            >
              <AppCard
                app={app}
                showStar={showStar}
                isFav={isFav(app.id)}
                onToggleFav={() => onToggleFav(app.id)}
                index={i}
              />
            </div>
            {onTogglePin ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePin(app.id);
                }}
                title={isPinned?.(app.id) ? 'Désépingler' : 'Épingler en accès rapide'}
                className={`absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full transition-all duration-150 touch-manipulation ${
                  !canPin && !isPinned?.(app.id) ? 'pointer-events-none opacity-0' : 'opacity-0 group-hover/card:opacity-100'
                } ${
                  isPinned?.(app.id)
                    ? 'bg-brand-100 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400'
                    : 'bg-white text-slate-400 shadow-sm dark:bg-slate-800 dark:text-slate-500'
                }`}
              >
                <Pin size={9} strokeWidth={2} />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
